<script setup>
import moment from "moment/moment";
import { onMounted, onUnmounted, ref, computed, watch } from "vue";
import axios from "axios";
import QuickMap from "@/components/home/dashboard/planting/QuickMap.vue";
import BarChart from "@/components/home/reporting/chartComponents/BarChart.vue";
import HorizontalBarChart from "@/components/home/reporting/chartComponents/HorizontalBarChart.vue";
import _ from "lodash";

const plantings = ref([]);
const plantingTotal = ref(null);
const plantingSummary = ref([]);
const plantingBySuburb = ref(null);
const plantingByMonth = ref(null);
const plantingList = ref([]);
const suburbcollection = ref(null);
const suburbLabels = ref([]);
const suburbVacant = ref([]);
const suburbPlanted = ref([]);
const plantingMonthCollection = ref(null);
const monthLabels = ref([]);
const monthPlanted = ref([]);
const selectedSpecies = ref([]);
const species = ref(null);
const tasks = ref(null);
const sortBy = ref("total");
const sortByOrder = ref("desc");
const totalTrees = ref(0);
const showMap = ref(false);
const today = ref(moment().format("dddd Do MMM YYYY"));
const year = ref(parseInt(moment().format("YYYY")));
const currYear = ref(parseInt(moment().format("YYYY")));
const openActions = ref(false);

const plantingsSummaryByYear = computed(() => {
    if (!plantingSummary.value) return [];
    
    let filteredList;
    if (moment().year() === year.value) {
        filteredList = plantingSummary.value.filter(p => p.planting_year <= year.value);
    } else {
        filteredList = plantingSummary.value.filter(p => p.planting_year === year.value);
    }

    // Sort the filtered list
    return _.orderBy(filteredList, 
        [(obj) => {
            return parseInt(obj[sortBy.value], 10);
        }], 
        sortByOrder.value
    );
});

const calculatedTotalTrees = computed(() => {
    if (!plantingsSummaryByYear.value) return 0;
    
    if (moment().year() === year.value) {
        return plantingsSummaryByYear.value.reduce((acc, item) => acc + parseInt(item.total), 0);
    } else {
        return plantingsSummaryByYear.value.reduce((acc, item) => acc + parseInt(item.total), 0);
    }
});

// Watch for changes in calculatedTotalTrees and update totalTrees
watch(calculatedTotalTrees, (newValue) => {
    totalTrees.value = newValue;
}, { immediate: true });

const openActionsMenu = () => {
    openActions.value = !openActions.value;
};
const close = (e) => {
    // if (!this.$refs.actions.contains(e.target)) {
    //     openActions.value = false;
    // }
};
const exportSummary = (yr) => {
    window.open(`/api/planting-summary/export/${yr}`);
};
const exportPlantings = (yr) => {
    window.open(`/api/planting-addresses/export/${yr}`);
};
const getPlantings = async () => {
    axios
        .get("/api/tree/planting/list")
        .then((response) => {
            plantings.value = response.data.trees;
            plantingSummary.value = response.data.speciesTotals;
            plantingBySuburb.value = response.data.suburbPlanting;
            plantingByMonth.value = response.data.plantingByMonth;
            plantingTotal.value = plantings.value.length;
            species.value = response.data.species;
            plantingChart();
        })
        .catch((error) => {
            console.error('Error fetching plantings:', error);
        });
};
const plantingChart = () => {
    plantingBySuburb.value.map((s) => {
        suburbLabels.value.push(s.name);
        suburbVacant.value.push(s.spaces);
        suburbPlanted.value.push(s.planted);
    });

    plantingByMonth.value.map((s) => {
        monthLabels.value.push(s.month + " '" + s.year.substring(s.year.length - 2));
        monthPlanted.value.push(s.total);
    });

    fillData();
};
const fillData = () => {
    suburbcollection.value = {
        labels: suburbLabels.value,
        datasets: [
            {
                label: "Approved Location",
                backgroundColor: "rgba(49, 130, 206,0.6)",
                borderColor: "rgba(49, 130, 206,0.3)",
                pointBackgroundColor: "rgba(49, 130, 206,0.0)",
                data: suburbVacant.value,
            },
            {
                label: "Trees Planted",
                backgroundColor: "rgba(76, 174, 79, 0.8)",
                borderColor: "rgba(76, 174, 79, 0.3)",
                pointBackgroundColor: "rgba(76, 174, 79, 0.0)",
                data: suburbPlanted.value,
            },
        ],
    };

    plantingMonthCollection.value = {
        labels: monthLabels.value,
        datasets: [
            {
                label: "Plantings by Month",
                backgroundColor: "rgba(76, 174, 79, 0.8)",
                borderColor: "rgba(76, 174, 79, 0.3)",
                pointBackgroundColor: "rgba(76, 174, 79, 0.0)",
                data: monthPlanted.value,
            },
        ],
    };
};
const toggleToArray = (speciesId) => {
    if (selectedSpecies.value.includes(speciesId)) {
        selectedSpecies.value.splice(selectedSpecies.value.indexOf(speciesId), 1);
    } else {
        selectedSpecies.value.push(speciesId);
    }
};
onMounted(() => {
    getPlantings();
    document.addEventListener("click", close.value);
});
onUnmounted(() => {
    document.removeEventListener("click", close.value);
});
</script>

<template>
    <div class="flex flex-wrap lg:flex-no-wrap w-full lg:w-3/4 lg:p-4">
        <div class="h-auto bg-white forestree-box all-rounded w-full lg:ml-6">
            <div class="bg-white rounded shadow-sm px-4 lg:px-8 mt-6 pb-6">
                <div class="flex flex-wrap lg:flex-no-wrap my-6 py-4 w-full border-b border-slate-300">
                    <span
                        @click.prevent="year = currYear"
                        :class="year === currYear ? 'text-blue-700 rounded-lg bg-slate-100' : 'text-slate-500'"
                        class="w-full text-center md:w-auto mr-0 md:mr-3 p-3 font-semibold cursor-pointer hover:text-blue-600"
                    >
                        {{ currYear }}
                    </span>
                    <span
                        v-for="n in 5"
                        :key="n"
                        @click.prevent="year = currYear + n"
                        :class="year === currYear + n ? 'text-blue-700 rounded-lg bg-slate-100' : 'text-slate-500'"
                        class="w-full text-center md:w-auto mr-0 md:mr-3 p-3 font-semibold cursor-pointer hover:text-blue-600"
                    >
                        {{ currYear + n }}
                    </span>
                    <span class="hidden md:block border-slate-500 border-l mr-3"></span>
                    <span
                        @click.prevent="showMap = !showMap"
                        :class="showMap ? 'text-green-700 rounded-lg bg-slate-100' : 'text-slate-500'"
                        class="w-full text-center md:w-auto mr-0 md:mr-3 p-3 font-semibold cursor-pointer hover:text-green-600"
                    >
                        Toggle Map
                    </span>
                </div>

                <div class="flex flex-wrap lg:flex-no-wrap">
                    <div class="w-full lg:w-2/3">
                        <p class="my-3 text-xl text-slate-600">{{ year }} Approved Species Summary</p>
                        <p class="my-3 text-md font-semibold text-orange-600">
                            <span v-if="plantingsSummaryByYear"
                                >{{ totalTrees }} planting spaces approved, {{ plantingsSummaryByYear.length }} unique preferred Species selected.</span
                            >
                        </p>
                        <p class="my-3 text-md text-slate-500">
                            All Planting Locations that have been assessed and approved. Click the table headers to sort the list.
                        </p>
                    </div>
                    <div class="w-full lg:w-1/3 text-right text-slate-600">
                        <div>
                            <button
                                ref="actions"
                                @click.prevent="openActionsMenu()"
                                class="bg-slate-100 hover:bg-slate-200 font-semibold text-blue-700 hover:text-blue-600 p-3 rounded-lg"
                            >
                                CSV Export
                            </button>

                            <div
                                v-if="openActions"
                                class="w-full relative h-0 right-0"
                                role="menu"
                                aria-orientation="vertical"
                                aria-labelledby="menu-button"
                                tabindex="-1"
                            >
                                <div
                                    class="mt-1 py-1 z-10 absolute right-0 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none w-56"
                                    role="none "
                                >
                                    <div
                                        @click="
                                            exportSummary(year);
                                            openActions = false;
                                        "
                                        class="cursor-pointer hover:bg-slate-100 text-slate-700 block px-4 py-2 text-sm"
                                        role="menuitem"
                                        tabindex="-1"
                                        id="menu-item-0"
                                    >
                                        Planting Summary
                                    </div>
                                    <div
                                        @click="
                                            exportPlantings(year);
                                            openActions = false;
                                        "
                                        class="cursor-pointer hover:bg-slate-100 text-slate-700 block px-4 py-2 text-sm"
                                        role="menuitem"
                                        tabindex="-1"
                                        id="menu-item-1"
                                    >
                                        Plantings with Addresses
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>


                <quick-map v-if="showMap" :year="year" :selectedSpecies="selectedSpecies" :species="species" :getPlantings="getPlantings" />

                <div class="ftree-table hovered main-first-cell w-full overflow-x-auto pt-4">
                    <div class="thead">
                        <div class="tr py-2">
                            <div
                                @click.prevent="
                                    sortBy = 'total';
                                    sortByOrder = 'desc';
                                "
                                :class="sortBy === 'total' ? 'text-blue-600' : 'text-slate-700'"
                                class="uppercase items-center w-1/4 lg:w-1/6 cursor-pointer"
                            >
                                Quantity
                            </div>
                            <div
                                @click.prevent="
                                    sortBy = 'genus';
                                    sortByOrder = 'asc';
                                "
                                :class="sortBy === 'genus' ? 'text-blue-600' : 'text-slate-700'"
                                class="uppercase items-center w-1/2 lg:w-4/6 cursor-pointer"
                            >
                                Species
                            </div>
                            <div
                                @click.prevent="
                                    sortBy = 'planted_total';
                                    sortByOrder = 'desc';
                                "
                                :class="sortBy === 'planted_total' ? 'text-blue-600' : 'text-slate-700'"
                                class="uppercase items-center w-1/2 lg:w-1/6 cursor-pointer"
                            >
                                Planted
                            </div>
                        </div>
                    </div>
                    <div class="tbody text-left text-slate-700">
                        <div
                            v-for="(tree, index) in plantingsSummaryByYear"
                            :key="index"
                            @click.prevent="toggleToArray(tree.species_id)"
                            :class="selectedSpecies.includes(tree.species_id) ? 'bg-green-200' : 'bg-white hover:bg-slate-100'"
                            class="tr border-b border-slate-200 cursor-pointer"
                        >
                            <div class="py-3 w-1/4 lg:w-1/6 items-center text-slate-600 font-bold">{{ tree.total }}</div>
                            <div class="py-3 w-1/2 lg:w-4/6 items-center">
                                <span class="py-2 font-bold">{{ tree.genus }}</span>
                                <span v-if="tree.species && tree.species !== '-'" class="italic pl-1">{{ tree.species }}</span>
                                <span v-if="tree.variety && tree.variety !== '-'" class="pl-1">'{{ tree.variety }}'</span>
                                <span v-if="tree.common_name && tree.common_name !== '-'" class="pl-1">({{ tree.common_name }}) </span>
                            </div>
                            <div :class="tree.planted_total > 0 ? 'font-semibold' : 'text-slate-400'" class="py-3 w-1/2 lg:w-1/6 flex items-center">
                                {{ tree.planted_total }}
                                <img v-if="parseInt(tree.planted_total) >= parseInt(tree.total)" class="ml-2 h-4 w-4" src="/images/icons/complete.png" />
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="plantingMonthCollection && year === currYear" class="pt-16 text-slate-700">
                    <hr />
                    <dl class="relative w-full">
                        <p class="my-3 text-xl text-slate-600">Plantings by Month</p>
                        <p class="my-3 pb-3 text-md text-slate-500">This graph displays the number of planting by month for the last 12 months.</p>

                        <bar-chart :data="plantingMonthCollection" :height="250" class="w-full mx-auto"></bar-chart>
                    </dl>
                </div>

                <div v-if="suburbcollection && year === currYear" class="pt-16 text-slate-700">
                    <hr />
                    <dl class="relative w-full">
                        <p class="my-3 text-xl text-slate-600">{{ currYear }} Plantings by Suburb</p>
                        <p class="my-3 pb-3 text-md text-slate-500">
                            This graph displays how many locations have been approved and planted by Suburb for {{ currYear }}.
                        </p>

                        <horizontal-bar-chart :data="suburbcollection"  class="w-full mx-auto"></horizontal-bar-chart>
                    </dl>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped></style>
